import React, { useEffect, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { usePlaidLink } from 'react-plaid-link';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { draftTokenField } from '@/utils/onboarding/draftToken';
import { toast } from 'sonner';
import ConnectorItem from './ConnectorItem';
import { DataConnector, LinkedConnector, StandardizedConnection } from '@/types/connections';
import { Loader2, X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface DatabaseConnectorViewProps {
  onBack: () => void;
  onConnectorCreated?: (connector: LinkedConnector) => Promise<void>;
  connectors: DataConnector[];
  loading: boolean;
  selectedConnector?: DataConnector | null;
  onConnectorSelect?: (connector: DataConnector) => void;
  onConnect?: () => Promise<void>;
  isConnecting?: boolean;
  error?: string | null;
  onLoadConnectors: () => Promise<void>;
  onSuccess?: (newConnector: StandardizedConnection) => Promise<void>;
  onBeginConnectionsRefresh?: () => void;
  shopName?: string;
  onShopNameChange?: (value: string) => void;
  merchantId?: string;
  onMerchantIdChange?: (value: string) => void;
  isVendor?: boolean | null;
  onIsVendorChange?: (value: boolean) => void;
}

interface PlaidInstitution {
  institution_id: string;
  name: string;
  logo?: string;
}

const sanitizeShopName = (value: string): string => {
  return value
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/, '');
};

const sanitizeMerchantId = (value: string): string => {
  return value.trim().toUpperCase();
};

const DatabaseConnectorView: React.FC<DatabaseConnectorViewProps> = ({
  onBack,
  onConnectorCreated,
  connectors,
  loading: isLoading,
  selectedConnector: propSelectedConnector,
  onConnectorSelect: propOnConnectorSelect,
  onConnect: propOnConnect,
  isConnecting: propIsConnecting,
  error: loadError,
  onLoadConnectors,
  onSuccess,
  onBeginConnectionsRefresh,
  shopName = '',
  onShopNameChange,
  merchantId = '',
  onMerchantIdChange,
  isVendor = null,
  onIsVendorChange
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConnector, setSelectedConnector] = useState<DataConnector | null>(propSelectedConnector || null);
  const [selectedBank, setSelectedBank] = useState<PlaidInstitution | null>(null);
  const [isLoadingBanks, setIsLoadingBanks] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isConnecting, setIsConnecting] = useState(propIsConnecting || false);
  const [isConnectingBank, setIsConnectingBank] = useState(false);
  const [bankLoadError, setBankLoadError] = useState<string | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('ecommerce');

  // Ref to track when a connection has just completed
  const justCompletedConnection = useRef(false);

  const { open: openPlaid, ready } = usePlaidLink({
    env: window.location.host === 'onboarding.figabl.com' ? 'production' : 'sandbox',
    token: linkToken,
    onSuccess: async (public_token, metadata) => {
      console.log('Plaid onSuccess called with:', { public_token, metadata });

      onBeginConnectionsRefresh?.();

      // Set completion flag immediately to prevent re-opening
      justCompletedConnection.current = true;

      setIsConnecting(true);
      try {
        const savedData = sessionStorage.getItem('figabl_company_data');
        let organisationId
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          organisationId = parsedData?.organization_id
        }

        // Clear linkToken BEFORE calling onLoadConnectors to prevent race condition
        setLinkToken(null);
        setIsConnectingBank(false);
        setIsLoadingBanks(true)

        const { data, error } = await invokeFunction(supabase, 'plaid-exchange-token', {
          body: {
            public_token,
            metadata,
            organisationId,
            ...draftTokenField(organisationId),
          }
        });

        if (error) throw error;

        toast.success(`Successfully connected to ${metadata.institution.name}`);

        if (onSuccess) {
          await onSuccess(data?.connection);
        }
      } catch (error) {
        console.error('Error exchanging token:', error);
        toast.error('Failed to connect bank account');
      } finally {
        setIsConnecting(false);
        setIsLoadingBanks(false)

        // Reset the completion flag after a delay to allow normal operations later
        setTimeout(() => {
          justCompletedConnection.current = false;
        }, 1000);
      }
    },
    onEvent: (eventName, metadata) => {
      console.log('Plaid event:', eventName, metadata);
    },
    onExit: (err, metadata) => {
      console.log('Plaid onExit called:', { err, metadata });
      setIsConnecting(false);
      setIsConnectingBank(false);
      // Clear linkToken to prevent re-opening
      setLinkToken(null);
      if (err) {
        console.error('Plaid error:', err);
        toast.error('Bank connection cancelled or failed');
      }
    }
  });

  // Effect to open Plaid Link when token is ready
  useEffect(() => {
    if (linkToken && ready && !justCompletedConnection.current) {
      openPlaid();
    }
  }, [linkToken, ready, openPlaid]);

  // Load ecommerce connectors when tab is active
  useEffect(() => {
    if ((activeTab === 'ecommerce' || activeTab === 'accountancy') && connectors.length === 0 && !isLoading && !loadError) {
      onLoadConnectors();
    }
  }, [onLoadConnectors, activeTab, connectors.length, isLoading, loadError]);

  // Filter connectors by category
  const getFilteredConnectors = () => {
    const ecommerceConnectors = ['Amazon Selling Partner', 'Google Sheets', 'NetSuite SuiteAnalytics', 'Shopify', 'Walmart Marketplace', 'Veeqo'];
    const accountancyConnectors = ['QuickBooks', 'Sage Intacct', 'NetSuite SuiteAnalytics', 'Xero'];

    if (activeTab === 'ecommerce') {
      return connectors.filter(connector => ecommerceConnectors.includes(connector.name));
    } else if (activeTab === 'accountancy') {
      return connectors.filter(connector => accountancyConnectors.includes(connector.name));
    }

    return connectors;
  };

  const filteredConnectors = getFilteredConnectors();

  // Update state when props change
  useEffect(() => {
    if (propSelectedConnector) {
      setSelectedConnector(propSelectedConnector);
    }
  }, [propSelectedConnector]);

  useEffect(() => {
    if (propIsConnecting !== undefined) {
      setIsConnecting(propIsConnecting);
    }
  }, [propIsConnecting]);


  const handleConnectorSelect = (connector: DataConnector) => {
    setSelectedConnector(connector);
    if (propOnConnectorSelect) {
      propOnConnectorSelect(connector);
    }
  };

  const handleConnect = async () => {
    if (!selectedConnector) return;
    try {
      if (propOnConnect) {
        await propOnConnect();
      }
    } catch (error) {
      console.error('Failed to connect:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Connect Data Sources</h2>
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="ecommerce">Ecommerce</TabsTrigger>
          <TabsTrigger value="banking">Banking</TabsTrigger>
          <TabsTrigger value="accountancy">Accountancy</TabsTrigger>
        </TabsList>

        <TabsContent value="ecommerce" className="space-y-4">
          {isLoading && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading connectors...</span>
            </div>
          )}

          {loadError ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-6">
              <div className="text-destructive mb-4">
                <X size={48} className="mx-auto mb-2" />
                <h3 className="text-xl font-medium">Connection Error</h3>
              </div>
              <p className="text-muted-foreground mb-4">
                {loadError || "Unable to load connectors. The API service might be unavailable."}
              </p>
              <Button
                onClick={onLoadConnectors}
              >
                Retry
              </Button>
            </div>
          ) : !isLoading && filteredConnectors.length === 0 ? (
            <div className="text-center py-12 px-6">
              <div className="mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h4 className="text-lg font-medium mb-2">No connectors available</h4>
              <p className="text-muted-foreground max-w-md mx-auto mb-6">
                We couldn't find any available connectors. Please try again later or contact support if this issue persists.
              </p>
              <Button variant="outline" onClick={onBack}>
                Go Back
              </Button>
            </div>
          ) : (
            <ScrollArea className="h-64">
              <div className="space-y-2">
                {filteredConnectors.map((connector) => (
                  <ConnectorItem
                    key={connector.id}
                    connector={connector}
                    isSelected={selectedConnector?.id === connector.id}
                    onSelect={handleConnectorSelect}
                    disabled={isConnecting}
                  />
                ))}
              </div>
            </ScrollArea>
          )}

          {selectedConnector && (
            <div className="space-y-4 pt-4">
              {(selectedConnector.id === 'shopify' || selectedConnector.name === 'Shopify') && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Shopify store name</label>
                  <Input
                    placeholder="my-store.myshopify.com"
                    value={shopName}
                    onChange={(e) => onShopNameChange?.(sanitizeShopName(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter your Shopify store domain (e.g. my-store.myshopify.com). You can paste the full URL.
                  </p>
                </div>
              )}
              {(selectedConnector.id === 'amazon_selling_partner' || selectedConnector.name === 'Amazon Selling Partner') && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Which Amazon account do you have?</label>
                    <RadioGroup
                      value={isVendor === null ? undefined : isVendor ? 'vendor' : 'seller'}
                      onValueChange={(value) => onIsVendorChange?.(value === 'vendor')}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="seller" id="amazon-account-seller" />
                        <label htmlFor="amazon-account-seller" className="text-sm font-normal">
                          Seller Central
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="vendor" id="amazon-account-vendor" />
                        <label htmlFor="amazon-account-vendor" className="text-sm font-normal">
                          Vendor Central
                        </label>
                      </div>
                    </RadioGroup>
                    <p className="text-xs text-muted-foreground">
                      This can't be changed after the connection is created.
                    </p>
                  </div>
                  {isVendor !== null && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        {isVendor ? 'Amazon Vendor Code' : 'Amazon Merchant ID'}
                      </label>
                      <Input
                        placeholder={isVendor ? 'ABCDE' : 'A1B2C3D4E5F6G7'}
                        value={merchantId}
                        onChange={(e) => onMerchantIdChange?.(sanitizeMerchantId(e.target.value))}
                      />
                      <p className="text-xs text-muted-foreground">
                        {isVendor
                          ? 'Find this in Vendor Central under Settings → Account Settings — your vendor/party code.'
                          : 'Find this in Seller Central under Settings → Account Info → Merchant Token.'}
                      </p>
                    </div>
                  )}
                </>
              )}
              <div className="flex gap-2">
                <Button
                  onClick={handleConnect}
                  disabled={
                    isConnecting ||
                    ((selectedConnector.id === 'shopify' || selectedConnector.name === 'Shopify') && !shopName.trim()) ||
                    ((selectedConnector.id === 'amazon_selling_partner' || selectedConnector.name === 'Amazon Selling Partner') && (isVendor === null || !merchantId.trim()))
                  }
                  className="flex-1"
                >
                  {isConnecting ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Connecting...
                    </>
                  ) : (
                    'Connect'
                  )}
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="banking" className="space-y-4">
          {isLoadingBanks && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading connectors...</span>
            </div>
          )}
          {!isLoadingBanks && <div className="flex flex-col items-center justify-center h-64 space-y-4">
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-2">Connect Your Bank</h3>
              <p className="text-muted-foreground mb-6">
                Securely connect your bank account to sync transactions and balances
              </p>
            </div>
            <Button
              onClick={async () => {
                try {
                  setIsConnectingBank(true);

                  const { data, error } = await invokeFunction(supabase, 'plaid-create-link-token', {
                    body: {
                      userId: (await supabase.auth.getUser()).data.user?.id,
                      institution_id: null,
                      products: ['transactions', 'assets'],
                      redirect_uri: window.location.href,
                    }
                  });

                  if (error) {
                    console.error('Error from plaid-create-link-token:', error);
                    throw error;
                  }

                  setLinkToken(data.link_token);

                } catch (error) {
                  console.error('Error creating link token:', error);
                  toast.error('Failed to initialize bank connection');
                  setIsConnectingBank(false);
                }
              }}
              disabled={isConnectingBank}
              size="lg"
              className="min-w-[200px]"
            >
              {isConnectingBank ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Connecting...
                </>
              ) : (
                'Connect Bank Account'
              )}
            </Button>
          </div>}
        </TabsContent>

        <TabsContent value="accountancy" className="space-y-4">
          {isLoading && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading connectors...</span>
            </div>
          )}

          {loadError ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-6">
              <div className="text-destructive mb-4">
                <X size={48} className="mx-auto mb-2" />
                <h3 className="text-xl font-medium">Connection Error</h3>
              </div>
              <p className="text-muted-foreground mb-4">
                {loadError || "Unable to load connectors. The API service might be unavailable."}
              </p>
              <Button
                onClick={onLoadConnectors}
              >
                Retry
              </Button>
            </div>
          ) : !isLoading && filteredConnectors.length === 0 ? (
            <div className="text-center py-12 px-6">
              <div className="mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h4 className="text-lg font-medium mb-2">No accountancy connectors available</h4>
              <p className="text-muted-foreground max-w-md mx-auto mb-6">
                We couldn't find any available accountancy connectors. Please try again later or contact support if this issue persists.
              </p>
              <Button variant="outline" onClick={onBack}>
                Go Back
              </Button>
            </div>
          ) : (
            <ScrollArea className="h-64">
              <div className="space-y-2">
                {filteredConnectors.map((connector) => (
                  <ConnectorItem
                    key={connector.id}
                    connector={connector}
                    isSelected={selectedConnector?.id === connector.id}
                    onSelect={handleConnectorSelect}
                    disabled={isConnecting}
                  />
                ))}
              </div>
            </ScrollArea>
          )}

          {selectedConnector && (
            <div className="space-y-4 pt-4">
              {(selectedConnector.id === 'shopify' || selectedConnector.name === 'Shopify') && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Shopify store name</label>
                  <Input
                    placeholder="my-store.myshopify.com"
                    value={shopName}
                    onChange={(e) => onShopNameChange?.(sanitizeShopName(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter your Shopify store domain (e.g. my-store.myshopify.com). You can paste the full URL.
                  </p>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  onClick={handleConnect}
                  disabled={isConnecting || ((selectedConnector.id === 'shopify' || selectedConnector.name === 'Shopify') && !shopName.trim())}
                  className="flex-1"
                >
                  {isConnecting ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Connecting...
                    </>
                  ) : (
                    'Connect'
                  )}
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default DatabaseConnectorView;